import { CommonModule, formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Input,
  OnInit,
  signal
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { TaxCalculatorComponent } from 'src/app/common/components/tax-calculator/tax-calculator.component';
import { EContactType, IContact } from 'src/app/common/model/contacts.model';
import { ICurrency } from 'src/app/common/model/currency.model';
import { IOpportunity } from 'src/app/common/model/opportunity.model';
import { IOrganization } from 'src/app/common/model/organization.model';
import { CreateContactComponent } from 'src/app/common/modules/contacts/components/create-contact/create-contact.component';
import { DeleteConfirmDialogComponent } from 'src/app/common/modules/shared/components/delete-confirm-dialog/deleteConfirmDialog.component';
import { ContactsService } from 'src/app/common/services/contacts/contacts.service';
import { OrganizationService } from 'src/app/common/services/organization/organization.service';
import { UtilService } from 'src/app/common/services/util/util.service';
import { isNil } from 'src/app/common/utils';
import { EItemType, IItem, ITax } from 'src/app/EBMSuite/models/item.model';
import {
  ETransactionType,
  ITransaction,
  ITransactionLineItem
} from 'src/app/EBMSuite/models/transaction.model';
import { ItemsService } from 'src/app/EBMSuite/services/items/items.service';
import { SalesService } from 'src/app/EBMSuite/services/sales/sales.service';
import { CustomersSearchComponent } from '../../ebmsuite-shared/components/customer-search/customers-search.component';
import { InvoiceDatepickerComponent } from '../../ebmsuite-shared/components/invoice-datepicker/invoice-datepicker.component';
import { AddTaxSetupComponent } from '../../ebmsuite-shared/components/tax-setup/add-tax-setup/add-tax-setup.component';
import { AllItemsComponent } from '../../items/components/all-items/all-items.component';
import { AddLineItemUtil } from './add-line-item.util';

interface AutoCompleteCompleteEvent {
  originalEvent: Event;
  query: string;
}

@Component({
  selector: 'app-transaction-create',
  standalone: true,
  templateUrl: './transaction-create.component.html',
  styleUrls: ['./transaction-create.component.scss'],
  providers: [DialogService, MessageService, ConfirmationService],
  imports: [
    CommonModule,
    ToastModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    AutoCompleteModule,
    CalendarModule,
    TableModule,
    MultiSelectModule,
    DropdownModule,
    InputTextModule,
    InputTextareaModule,
    TooltipModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TransactionCreateComponent implements OnInit {
  readonly ref = inject(DynamicDialogRef);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly salesService = inject(SalesService);
  readonly utilService = inject(UtilService);
  readonly contactService = inject(ContactsService);
  readonly dialogService = inject(DialogService);
  readonly itemService = inject(ItemsService);
  readonly messageService = inject(MessageService);
  readonly organizationService = inject(OrganizationService);
  readonly contactsService = inject(ContactsService);
  readonly addLineItemUtil = inject(AddLineItemUtil);

  @Input() transactionType!: ETransactionType;
  @Input() opportunity!: IOpportunity;

  transactionTypeEnum = ETransactionType;
  transactionToEdit!: ITransaction | null;
  subscription = new Subscription();

  showDiscount = false;

  transactionEditModel: any = {
    transactionDate: formatDate(new Date(), 'MM/dd/yyyy', 'en')
  };
  selectedItems = signal<ITransactionLineItem[]>([]);
  taxableItems: ITransactionLineItem[] = [];
  totalTax = signal<number>(0);
  subtotalTax = signal<number>(0);
  todayDate = new Date();

  searchText = '';
  customers = signal<IContact[]>([]);
  selectedContact!: any;
  rows = 50;
  defaultPagination = {
    page: 0,
    pageSize: this.rows,
    searchParam: ''
  };
  isEdit = false;
  currencyList = signal<ICurrency[]>([]);
  formattedCurrencyList = computed(() => {
    const list = this.currencyList();
    if (!list.length) return [];

    return list.map((data) => ({
      ...data,
      formattedLabel: `${data.code}` + (data.symbol ? `(${data.symbol})` : ''),
      currencySymbolOrCode: {
        symbol: data.symbol ? data.symbol : '',
        code: data?.code
      },
      code: data?.code
    }));
  });
  defaultCurrency!: { symbol: string; code: string };
  taxes: ITax[] = [];

  transactionForm = new FormGroup({
    customer: new FormControl<IContact | null>(null, [Validators.required]),
    transactionDate: new FormControl(formatDate(new Date(), 'MM/dd/yyyy', 'en'), [
      Validators.required
    ]),
    transactionDueDate: new FormControl(''),
    currency: new FormControl(this.defaultCurrency, Validators.required),
    customerNotes: new FormControl(''),
    transactionTotalTax: new FormControl(0, Validators.required),
    transactionUniqueCode: new FormControl(''),
    conversionRate: new FormControl(1, Validators.required),
    discountType: new FormControl('amount'), // At root level
    discount: new FormGroup({
      description: new FormControl(''),
      amount: new FormControl(null),
      percentage: new FormControl(null)
    })
  });

  showSearchCustomerDropdown = signal(false);
  currentOrganization!: IOrganization;
  transactionTagLine = 'Thank you for your business';
  editTransactionTagLine = false;

  discountTypes = [
    {
      label: '%',
      value: 'percentage'
    },
    {
      label: 'Amount',
      // label: this.transactionForm.value.currency?.symbol
      //     ? this.transactionForm.value.currency.symbol
      //     : this.utilService.getCurrentCurrency(),
      value: 'amount'
    }
  ];
  selectedCustomer: any = null;

  discountValue$: Observable<number> = this.transactionForm.valueChanges.pipe(
    map(() => this.getDiscountValue()) // Recalculate on form change
  );
  expectedReturn = signal<number>(0);
  expectedReturnPercentage = signal<number>(0);

  isEditingConversionRate = false;
  tempConversionRate: number | null | undefined = null;

  constructor() {
    effect(() => {
      if (!this.showSearchCustomerDropdown()) {
        this.transactionForm.get('customer')?.patchValue(null);
      }
    });
  }

  ngOnInit(): void {
    this.getTransactionIdFromParam();
    this.getCurrency();
    this.getOrganizationTaxes();
    this.getCurrentOrganization();
    this.getPaginatedCustomers();

    this.transactionForm.get('discountType')?.valueChanges.subscribe((discountType) => {
      this.updateDiscountValue(discountType);
    });

    this.transactionForm.get('currency')?.valueChanges.subscribe(() => {
      this.getConversionRate();
    });

    this.transactionForm?.valueChanges
      .pipe(
        debounceTime(5000),
        tap({
          next: () => {
            this.calculateTaxTotal();
          }
        })
      )
      .subscribe({
        error: (e) =>
          console.warn('[transaction-create] Error in valueChanges subscription:', e)
      });
  }

  getTransactionIdFromParam(): void {
    this.subscription.add(
      this.activatedRoute.params.subscribe({
        next: (param) => {
          const transactionId = param['transactionId'];
          const leadId = param['leadId'];

          if (transactionId) {
            if (
              this.utilService.doesURLHaveText('edit-invoice') ||
              this.utilService.doesURLHaveText('quotes')
            ) {
              this.isEdit = true;
            }

            this.getTransactionDetails(transactionId);
          }

          if (leadId) {
            this.getLeadDetails(leadId);
          } else if (!transactionId) {
            this.generateTransactionUniqueCode(this.transactionType);
          }

          if (this.opportunity) {
            // this.transactionForm.get('customer')?.patchValue(this.opportunity.contact);
            this.selectedItems.set(
              this.opportunity.relatedProducts.map((item) => ({
                name: item.name,
                description: item.description,
                itemPrice: item.price,
                itemCost: item.cost,
                quantity: 1,
                item: item._id,
                isTaxed: true,
                taxes: item.taxes
              }))
            );

            this.calculateSubtotal();
          }

          if (!this.isEdit) {
            this.transactionForm
              .get('currency')
              ?.setValue(this.utilService.getCurrentCurrency());
          }
        }
      })
    );
  }

  generateTransactionUniqueCode(transactionType: ETransactionType): void {
    this.subscription.add(
      this.salesService.generateAndSaveTransactionUniqueCode(transactionType).subscribe({
        next: (res) => {
          this.transactionForm.get('transactionUniqueCode')?.patchValue(res.result);
        }
      })
    );
  }

  getTransactionDetails(transactionId: string): void {
    const getTransactionByIdRoute =
      this.transactionType === ETransactionType.INVOICE
        ? this.salesService.getInvoiceById(transactionId)
        : this.salesService.getQuoteById(transactionId);
    this.subscription.add(
      getTransactionByIdRoute.subscribe({
        next: (res) => {
          this.transactionToEdit = res.result;
          this.showDiscount = true;
          this.defaultCurrency = this.transactionToEdit.transactionHeader.currency;
          this.transactionForm.get('currency')?.patchValue(this.defaultCurrency);

          const transactionToEditHeader = this.transactionToEdit?.transactionHeader;
          this.transactionEditModel = {
            ...transactionToEditHeader,
            customer: transactionToEditHeader?.customer,
            transactionDate: formatDate(
              new Date(
                transactionToEditHeader?.transactionDate
                  ? transactionToEditHeader?.transactionDate
                  : new Date()
              ),
              'MM/dd/yyyy',
              'en'
            ),
            transactionDueDate: formatDate(
              new Date(
                transactionToEditHeader?.transactionDueDate
                  ? transactionToEditHeader?.transactionDueDate
                  : new Date()
              ),
              'MM/dd/yyyy',
              'en'
            ),
            currency: transactionToEditHeader?.currency,
            transactionUniqueCode: transactionToEditHeader?.transactionUniqueCode,
            discount: transactionToEditHeader?.discount,
            discountType: !isNil(transactionToEditHeader?.discount?.percentage)
              ? 'percentage'
              : 'amount'
          };

          this.transactionForm.patchValue(this.transactionEditModel);
          this.selectedItems.set(this.transactionToEdit?.transactionLineItems || []);
          this.calculateSubtotal();
        }
      })
    );
  }

  addTransactionLineItem(transactionLineItem: any): void {
    const transactionLineItemToUpdate = {
      ...transactionLineItem,
      transaction: this.transactionToEdit?.transactionHeader._id
    };
    this.subscription.add(
      this.salesService.addTransactionLineItem(transactionLineItemToUpdate).subscribe({
        error: (e) => console.dir(e)
      })
    );
  }

  generateTransactionFunc(): void {
    const selectedItems = this.selectedItems().map((x) => ({
      ...x,
      taxes: x.taxes?.map((y) => y._id)
    }));
    const transaction = {
      ...this.transactionForm.value,
      transactionLineItems: [...selectedItems],
      currency: this.transactionForm.value.currency
    };

    const generateTransactionRoute =
      this.transactionType === ETransactionType.INVOICE
        ? this.salesService.generateInvoice(transaction)
        : this.transactionType === ETransactionType.QUOTE && this.opportunity !== null
          ? this.salesService.generateQuoteForOpportunity(
              transaction,
              this.opportunity._id!
            )
          : this.salesService.generateQuote(transaction);

    generateTransactionRoute.subscribe({
      next: (res) => {
        let redirectURL = '';
        if (this.transactionType === ETransactionType.INVOICE) {
          redirectURL =
            '/app/invoice/invoice-details/' + res.result?.transactionHeader?._id;
        } else if (
          this.transactionType === ETransactionType.QUOTE &&
          this.opportunity === null
        ) {
          redirectURL = '/app/quotes/quote-details/' + res.result?.transactionHeader?._id;
        } else if (
          this.transactionType === ETransactionType.QUOTE &&
          this.opportunity?._id !== null
        ) {
          this.ref.close(res);
        }
        if (redirectURL) {
          this.router.navigateByUrl(redirectURL);
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error.message
        });
      }
    });
  }

  updateTransaction(): void {
    this.updateTransactionLineItem();
    const editedTransaction = {
      ...this.transactionForm.value
    };

    const updateTransactionRoute =
      this.transactionType === ETransactionType.INVOICE
        ? this.salesService.updateInvoice(
            editedTransaction,
            this.transactionToEdit?.transactionHeader?._id
          )
        : this.salesService.updateQuote(
            editedTransaction,
            this.transactionToEdit?.transactionHeader?._id
          );

    this.subscription.add(
      updateTransactionRoute.subscribe({
        next: (res) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: res.message
          });
          this.utilService.goBack();
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err.message
          });
        }
      })
    );
  }

  getPaginatedCustomers(): void {
    this.subscription.add(
      this.contactService.getPaginatedCustomers(this.defaultPagination).subscribe({
        next: (res) => {
          this.customers.set(res.result.data);
        }
      })
    );
  }

  getCustomerSearchResults(searchEvent?: AutoCompleteCompleteEvent): void {
    if (searchEvent?.query) {
      this.defaultPagination = {
        ...this.defaultPagination,
        searchParam: searchEvent?.query
      };
      this.getPaginatedCustomers();
    } else {
      this.defaultPagination = {
        ...this.defaultPagination,
        searchParam: ''
      };
      this.getPaginatedCustomers();
    }
  }

  openSearchCustomer(): void {
    const ref = this.dialogService.open(CustomersSearchComponent, {
      header: 'Change Customer',
      height: '40%',
      width: '50%',
      data: { contactType: EContactType.CUSTOMER }
    });
    ref.onClose.subscribe((addedCustomer) => {
      if (addedCustomer) {
        this.transactionForm.get('customer')?.patchValue(addedCustomer);
        ref.close();
      }
    });
  }

  openAddCustomerComponent() {
    const ref = this.dialogService.open(CreateContactComponent, {
      header: 'Add a new customer',
      height: '70%',
      width: '80%',
      breakpoints: {
        '960px': '90%',
        '640px': '95%'
      },
      data: { contactType: EContactType.CUSTOMER }
    });
    ref.onClose.subscribe((addedCustomer) => {
      if (addedCustomer) {
        this.transactionForm.get('customer')?.patchValue(addedCustomer);
      }
    });
  }

  getItemById(item: any): void {
    this.subscription.add(
      this.itemService.getItem(item.item).subscribe({
        next: (res) => {
          const itemById = res.result;
          if (itemById.quantity && itemById.trackInventory) {
            if (item.quantity > itemById?.quantity) {
              item.quantity = itemById.quantity;
              this.messageService.add({
                severity: 'warn',
                summary: 'Item quantity',
                detail: `The new quantity it higher than ${itemById?.name} in stock (${itemById.quantity}). We have adjusted the quantity to reflect all available items`,
                life: 10000
              });
            }
          } else if (
            itemById.inStock === 'OUTOFSTOCK' &&
            itemById.itemType === EItemType.INVENTORYITEM &&
            itemById.trackInventory
          ) {
            item.quantity = itemById.quantity;
            this.messageService.add({
              severity: 'error',
              summary: 'Item quantity',
              detail: `${itemById?.name} is OUTOFSTOCK`,
              life: 10000
            });
          }
        }
      })
    );
  }

  updateTransactionLineItem(): void {
    this.selectedItems().forEach((x, index, array) => {
      const updatedItemWithTax = {
        ...x,
        taxes: x.taxes?.map((y) => y._id)
      } as ITransactionLineItem;
      if (x._id) {
        this.subscription.add(
          this.salesService.updateTransactionLineItem(updatedItemWithTax).subscribe({
            complete: () => {
              if (index === array.length - 1) {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Item quantity',
                  detail: `The new quantity has been updated successfully`,
                  life: 10000
                });
              }
            }
          })
        );
      }
    });
  }

  openConfirmDeleteDialog(transactionLineItem: ITransactionLineItem) {
    const ref = this.dialogService.open(DeleteConfirmDialogComponent, {
      header: 'Delete Confirmation',
      width: '50%',
      baseZIndex: 10000
    });
    ref.onClose.subscribe((confirm) => {
      if (confirm && this.isEdit) {
        this.deleteTransactionLineItem(transactionLineItem);
      } else if (confirm && !this.isEdit) {
        this.selectedItems.set(
          this.selectedItems().filter((x) => x.item !== transactionLineItem.item)
        );

        this.calculateSubtotal();
        this.calculateTaxTotal();
      }
    });
  }

  openTaxCalculatorDialog(transactionLineItem: ITransactionLineItem) {
    const ref = this.dialogService.open(TaxCalculatorComponent, {
      header: `Reverse tax calculation on line item`,
      width: '50%',
      breakpoints: {
        '1024px': '75%',
        '768px': '97%'
      },
      data: {
        lineItem: transactionLineItem,
        currency: this.transactionForm.get('currency')?.getRawValue(),
        conversionRate: this.transactionForm.get('conversionRate')?.getRawValue()
      }
    });

    ref.onClose.subscribe((data) => {});
  }

  deleteTransactionLineItem(transactionLineItem: ITransactionLineItem): void {
    const transactionLineItemToDelete = {
      transactionId: this.transactionToEdit?.transactionHeader._id,
      transactionLineItemId: transactionLineItem._id,
      transactionType: this.transactionType,
      item: transactionLineItem.item
    };

    this.subscription.add(
      this.salesService
        .deleteTransactionLineItem([transactionLineItemToDelete])
        .subscribe({
          complete: () => {
            this.selectedItems.set(
              this.selectedItems().filter((x) => x.item !== transactionLineItem.item)
            );

            this.calculateSubtotal();
            this.calculateTaxTotal();

            this.calculateSubtotal();
            this.calculateTaxTotal();
          },
          error: (e) => {
            this.messageService.add({
              severity: 'error',
              summary: e.error.message || 'An error occurred, please try again',
              life: 10000
            });
          }
        })
    );
  }

  getData(editedValue: any): void {
    const itemNewValue = editedValue;
    if (this.transactionType === this.transactionTypeEnum.INVOICE) {
      this.getItemById(itemNewValue.data);
    }
    this.calculateSubtotal();
  }

  public openInvoiceDatePickerModal() {
    const ref = this.dialogService.open(InvoiceDatepickerComponent, {
      header: 'Set invoice due date',
      baseZIndex: 10000
    });
    ref.onClose.subscribe((invoiceDueDate) => {
      if (invoiceDueDate) {
        this.transactionForm.value.transactionDueDate = invoiceDueDate;
        const dueDate = formatDate(new Date(invoiceDueDate), 'MM/dd/yyyy', 'en');
        this.transactionForm.get('transactionDueDate')?.patchValue(dueDate);
      }
    });
  }

  updateTaxedItems(
    // action: InputSwitchOnChangeEvent,
    action: any,
    item: ITransactionLineItem
  ): void {
    if (action.checked) {
      this.taxableItems = [...this.taxableItems, item];
    } else {
      const itemIndex = this.taxableItems.findIndex((x) => x._id === item._id);
      this.taxableItems.splice(itemIndex, 1);
    }
    this.calculateSubtotal();
  }

  calculateSubtotal(): void {
    this.subtotalTax.set(
      this.selectedItems().reduce((subtotal, item) => {
        return subtotal + item.itemPrice * item.quantity;
      }, 0)
    );

    this.calculateTaxTotal();
    this.calculateExpectedReturn();
  }

  calculateTaxTotal(): void {
    const discountAmount = this.getDiscountValue();

    // Calculate the subtotal after discount
    // const subtotalAfterDiscount = this.subtotalTax - discountAmount;

    // Apply taxes on the discounted amount
    this.totalTax.set(
      this.selectedItems().reduce((taxes, item) => {
        let itemTax = 0;
        const taxArrLength = item.taxes ? item.taxes.length : 0;

        for (let i = 0; i < taxArrLength; i++) {
          // Calculate item's share of the total subtotal
          const itemTotal = item.itemPrice * item.quantity;
          const itemPercentage =
            this.subtotalTax() > 0 ? itemTotal / this.subtotalTax() : 0;

          // Apply the discount proportionally to this item
          const itemDiscountAmount = discountAmount * itemPercentage;
          const itemAfterDiscount = itemTotal - itemDiscountAmount;

          // Calculate tax based on the discounted amount
          const tax = (item.taxes![i].percentage * itemAfterDiscount) / 100;
          itemTax = itemTax + tax;
        }
        return taxes + itemTax;
      }, 0)
    );

    this.calculateExpectedReturn();
  }

  getCurrency(): void {
    this.subscription.add(
      this.organizationService.getCurrencies().subscribe({
        next: (res) => {
          this.currencyList.set(res.result);
          const organizationCurrency = this.currencyList().find(
            (x) =>
              x.symbol === this.utilService.getCurrentCurrency() ||
              x.code === this.utilService.getCurrentCurrency()
          );
          if (organizationCurrency && !this.isEdit) {
            const defaultCurrency = {
              code: organizationCurrency.code ? organizationCurrency.code : '',
              symbol: organizationCurrency.symbol ? organizationCurrency.symbol : ''
            };
            this.transactionForm.get('currency')?.patchValue(defaultCurrency);
          }
        }
      })
    );
  }

  getOrganizationTaxes(): void {
    this.subscription.add(
      this.organizationService.getAllOrganizationTaxes().subscribe({
        next: (res) => {
          this.taxes = res.result;
        }
      })
    );
  }

  getCurrentOrganization(): void {
    this.subscription.add(
      this.organizationService.getCurrentOrganization().subscribe((res) => {
        this.currentOrganization = res.result.organization;
        this.transactionTagLine =
          (this.transactionType === ETransactionType.INVOICE
            ? this.currentOrganization.printoutSettings?.invoice?.tagline
            : this.currentOrganization.printoutSettings?.quote?.tagline) ||
          'Thank you for your business';
      })
    );
  }

  updateTransactionTagLine(): void {
    const editedOrganization = {
      ...this.currentOrganization,
      printoutSettings: {
        [this.transactionType === ETransactionType.INVOICE ? 'invoice' : 'quote']: {
          tagline: this.transactionTagLine
        }
      }
    };

    this.subscription.add(
      this.organizationService.editOrganization(editedOrganization).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Successfully updated the tagline'
          });
          this.editTransactionTagLine = false;
          this.getCurrentOrganization();
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err.error.message
          });
          this.editTransactionTagLine = false;
          this.getCurrentOrganization();
        }
      })
    );
  }

  openAddTaxComponent(): void {
    const ref = this.dialogService.open(AddTaxSetupComponent, {
      header: 'Add a new tax',
      height: '90%',
      width: '80%'
    });
    ref.onClose.subscribe(() => {
      this.getOrganizationTaxes();
    });
  }

  getLeadDetails(leadId: string): void {
    this.subscription.add(
      this.contactsService.getLeadById(leadId).subscribe({
        next: (res) => {
          const leadDetails = res.result;
          this.transactionEditModel = {
            ...this.transactionEditModel,
            customer: leadDetails
          };
          this.transactionForm.patchValue(this.transactionEditModel);
        }
      })
    );
  }

  getConversionRate(): void {
    const selectedCurrency = this.transactionForm.get('currency')?.value
      ? this.transactionForm.get('currency')?.value?.code
      : this.utilService.getCurrentCurrency();

    const organizationCurrency = this.currencyList().find(
      (x) =>
        x.symbol === this.utilService.getCurrentCurrency() ||
        x.code === this.utilService.getCurrentCurrency()
    );

    const currencyToConvert = {
      fromCurrency: organizationCurrency?.code,
      toCurrency: selectedCurrency
    };
    this.salesService.getCurrencyConversionRate(currencyToConvert).subscribe({
      next: (res) => {
        this.transactionForm.patchValue({
          conversionRate: res.result.rate
        });
      }
    });
  }

  linkItemDialog() {
    const ref: DynamicDialogRef = this.dialogService.open(AllItemsComponent, {
      header: 'Select items to add to invoice',
      width: '75%',
      modal: true
    });
    ref.onClose.subscribe((checkedItems: IItem[]) => {
      if (!checkedItems) return;

      const processableItems = checkedItems.filter((item) => {
        if (
          item.trackInventory &&
          item.hasOwnProperty('quantity') &&
          this.transactionType === ETransactionType.INVOICE
        ) {
          if ((item.quantity || 0) < 1) {
            this.messageService.add({
              severity: 'error',
              summary: 'Item quantity',
              detail: `${item?.name} is OUTOFSTOCK`,
              life: 10000
            });
            return false;
          }
        }

        return true;
      });

      if (!processableItems.length) return;

      if (this.isEdit) {
        this.addLineItemUtil
          .onEdit(this.transactionToEdit?.transactionHeader._id as string, {
            checkedItems: processableItems,
            selectedItems: this.selectedItems(),
            taxableItems: this.taxableItems,
            transactionType: this.transactionType
          })
          .subscribe({
            next: (output) => {
              // Add this line to trigger change detection
              this.selectedItems.set([...output.selectedItems]);
              this.taxableItems = [...output.taxableItems];

              // Calculate subtotal after processing all items
              this.calculateSubtotal();
              this.calculateExpectedReturn();

              // Show confirmation message
              this.messageService.add({
                severity: 'success',
                summary: 'Items Added',
                detail: `Added ${checkedItems.length} items to the invoice`,
                life: 3000
              });
            },
            error: (e) => {
              this.messageService.add({
                severity: 'error',
                summary: e.error.message || 'An error occurred, please try again',
                life: 10000
              });
            }
          });
      } else {
        this.addLineItemUtil
          .onCreate({
            checkedItems: processableItems,
            selectedItems: this.selectedItems(),
            taxableItems: this.taxableItems,
            transactionType: this.transactionType
          })
          .subscribe({
            next: (output) => {
              // Add this line to trigger change detection
              this.selectedItems.set([...output.selectedItems]);
              this.taxableItems = [...output.taxableItems];

              // Calculate subtotal after processing all items
              this.calculateSubtotal();
              this.calculateExpectedReturn();

              // Show confirmation message
              this.messageService.add({
                severity: 'success',
                summary: 'Items Added',
                detail: `Added ${checkedItems.length} items to the invoice`,
                life: 3000
              });
            }
          });
      }
    });
  }

  removeDiscount() {
    const discountGroup = this.transactionForm.get('discount') as FormGroup;

    if (discountGroup) {
      discountGroup.get('description')?.setValue('');
      discountGroup.get('amount')?.setValue(null);
      discountGroup.get('percentage')?.setValue(null);
    }

    this.showDiscount = false;
  }

  getDiscountValue(): number {
    const discountGroup = this.transactionForm.get('discount') as FormGroup;
    const discountType = this.transactionForm.get('discountType')?.value;

    if (!discountGroup) return 0;

    if (discountType === 'amount') {
      const amount = discountGroup.get('amount')?.value;
      return amount ?? 0;
    } else if (discountType === 'percentage') {
      const percentage = discountGroup.get('percentage')?.value;
      return ((percentage ?? 0) * (this.subtotalTax() + this.totalTax())) / 100;
    }

    return 0;
  }

  updateDiscountValue(discountType: string | null): void {
    const discountGroup = this.transactionForm.get('discount') as FormGroup;

    if (discountGroup) {
      if (discountType === 'percentage') {
        discountGroup.get('amount')?.setValue(null);
      } else if (discountType === 'amount') {
        discountGroup.get('percentage')?.setValue(null);
      }
    }
  }

  startEditingConversionRate(): void {
    this.isEditingConversionRate = true;
    this.tempConversionRate = this.transactionForm.value.conversionRate;
  }

  saveConversionRate(): void {
    if (this.tempConversionRate !== null) {
      this.transactionForm.patchValue({
        conversionRate: this.tempConversionRate
      });
    }
    this.isEditingConversionRate = false;
  }

  cancelEditingConversionRate(): void {
    this.isEditingConversionRate = false;
  }

  calculateExpectedReturn(): void {
    // Calculate the base return (revenue - cost) for each item
    this.expectedReturn.set(
      this.selectedItems().reduce((totalReturn, item) => {
        // Get the cost from the item if available, otherwise assume cost is 70% of price
        const itemCost = item.itemCost || item.itemPrice * 0.7;
        const itemQuantity = item.quantity || 1;
        const itemRevenue = item.itemPrice * itemQuantity;
        const itemProfit = itemRevenue - itemCost * itemQuantity;
        return totalReturn + itemProfit;
      }, 0)
    );

    // Apply discount to the expected return
    const discountAmount = this.getDiscountValue();
    if (discountAmount > 0 && this.subtotalTax() > 0) {
      // Reduce profit by the discount amount
      this.expectedReturn.set(this.expectedReturn() - discountAmount);
    }

    // Calculate the percentage as (profit / revenue) * 100
    const totalRevenue = this.subtotalTax();
    if (totalRevenue > 0) {
      // Calculate margin percentage based on revenue before taxes and after discounts
      this.expectedReturnPercentage.set(
        (this.expectedReturn() / (totalRevenue - discountAmount)) * 100
      );
    } else {
      this.expectedReturnPercentage.set(0);
    }
  }
}
